/**
 * Missing Accounts Report
 *
 * Two sections:
 *  1. Raketech Unmatched — (account_id, offer) pairs from Raketech sheets with
 *     no local account mapping. Each offer is its own row so you can see the
 *     first/last FTD and first/last commission month per program.
 *     Enriched with Created Date, Account Status and Adwin Comment from the
 *     "Recon Accounts Not Verified" sheet when available.
 *  2. AC Only — Accounts in AC reporting not present locally (requires AC token).
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  UserX,
  Loader2,
  RefreshCw,
  Search,
  AlertTriangle,
  Key,
  Eye,
  EyeOff,
  Cloud,
  Database,
  TrendingDown,
  Info,
  CheckCircle2,
  ShieldAlert,
  FileText,
  ShieldCheck,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// AC token storage
// ---------------------------------------------------------------------------
const AC_TOKEN_KEY = 'affiliation-cloud-token';
const AC_TOKEN_EXPIRY_HOURS = 12;

interface StoredToken { token: string; expiresAt: number; }

const getStoredACToken = (): string | null => {
  try {
    const stored = localStorage.getItem(AC_TOKEN_KEY);
    if (stored) {
      const parsed: StoredToken = JSON.parse(stored);
      if (parsed.expiresAt > Date.now()) return parsed.token;
      localStorage.removeItem(AC_TOKEN_KEY);
    }
  } catch { localStorage.removeItem(AC_TOKEN_KEY); }
  return null;
};

const storeACToken = (token: string): void => {
  localStorage.setItem(AC_TOKEN_KEY, JSON.stringify({
    token, expiresAt: Date.now() + AC_TOKEN_EXPIRY_HOURS * 60 * 60 * 1000,
  }));
};

const getAdwstatsToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) { const p = JSON.parse(auth); return p.state?.token || null; }
  } catch { /* ignore */ }
  return null;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface MonthlyPoint { month: string; ftds: number; commission: number; }

/** One row per (account_id, offer) */
interface RaketechUnmatchedOffer {
  account_id: string;
  offer: string;
  username: string;
  partner: string;
  geo: string;
  brand: string;
  currency: string;
  total_recon_earnings_eur: number;
  total_partner_commission_eur: number;
  total_partner_commission_our_share_eur: number;
  // From raketech_verified_accounts
  ac_id: string | null;
  created_date: string | null;
  account_status: string | null;
  adwin_comment: string | null;
  verified_amount: number | null;
  verified_currency: string | null;
  // Timeline
  first_ftd_month: string | null;
  last_ftd_month: string | null;
  first_commission_month: string | null;
  last_commission_month: string | null;
  sources: ('reconciliation' | 'partner')[];
}

interface ACOnlyAccount {
  program_name: string;
  ac_id?: number;
  affiliate_account_ids: number[];
  total_commission: number;
  total_ftds: number;
  total_clicks: number;
  total_signups: number;
  first_ftd_month: string | null;
  last_ftd_month: string | null;
  first_commission_month: string | null;
  last_commission_month: string | null;
  monthly_data: MonthlyPoint[];
}

/** Offer from a monthly commission sheet not captured in reconciliation or partner accounts */
interface InvoiceOnlyOffer {
  partner: string;
  offer: string;
  brand_name: string;
  geo: string;
  total_ftds: number;
  total_qftds: number;
  total_cpa_revenue: number;
  total_rs_revenue: number;
  total_revenue_eur: number;
  avg_rs_pct: number;
  month_count: number;
  first_month: string | null;
  last_month: string | null;
  // Enriched from raketech_verified_accounts when offer or partner+geo matches
  username: string | null;
  ac_id: string | null;
  account_status: string | null;
  created_date: string | null;
  match_status: 'unmatched' | 'unknown';
}

interface ReportSummary {
  raketech_unmatched_count: number;
  raketech_unmatched_earnings_eur: number;
  ac_only_count: number;
  ac_only_commission_eur: number;
  invoice_only_count: number;
  invoice_only_revenue_eur: number;
  total_missing_commission_eur: number;
  ac_total_accounts: number;
  ac_token_provided: boolean;
  date_range: { from: string; to: string };
}

interface ReportData {
  summary: ReportSummary;
  raketech_unmatched: RaketechUnmatchedOffer[];
  ac_only: ACOnlyAccount[];
  invoice_only: InvoiceOnlyOffer[];
  ac_fetch_error: string | null;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
const fmtEUR = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtMonth = (m: string | null): string => {
  if (!m) return '—';
  try {
    // "YYYY-MM" format (standard)
    if (/^\d{4}-\d{2}$/.test(m)) {
      const [year, month] = m.split('-').map(Number);
      return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    // "MonthName YYYY" format (from invoice sheet names, e.g. "October 2025")
    const d = new Date(`1 ${m}`);
    if (!isNaN(d.getTime())) return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch { /* ignore */ }
  return m;
};

const fmtDate = (d: string | null): string => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return d; }
};

/** Normalise "October 2025" or "2025-10" → Date for recency comparison. */
const parseMonthToDate = (m: string): Date | null => {
  if (/^\d{4}-\d{2}$/.test(m)) {
    const [yr, mo] = m.split('-').map(Number);
    return new Date(yr, mo - 1, 1);
  }
  const d = new Date(`1 ${m}`);
  return isNaN(d.getTime()) ? null : d;
};

  const recencyClass = (month: string | null): string => {
    if (!month) return 'text-gray-400';
    const d = parseMonthToDate(month);
    if (!d) return 'text-gray-400';
    const now = new Date();
    const monthsAgo = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
    if (monthsAgo <= 3) return 'text-emerald-600';
    if (monthsAgo <= 12) return 'text-amber-600';
    return 'text-gray-500';
  };

// ---------------------------------------------------------------------------
// AC Live Check types
// ---------------------------------------------------------------------------
interface ACCheckRow {
  source: 'raketech_active' | 'missing_sheet';
  partner: string;
  offer: string;
  username: string;
  geo: string;
  brand_name: string;
  raketech_id: string | null;
  rec_report_id: string | null;
  account_status: string | null;
  created_date: string | null;
  first_ftd_month: string | null;
  last_ftd_month: string | null;
  first_commission_month: string | null;
  last_commission_month: string | null;
  in_ac: boolean;
  ac_name: string | null;
  ac_status: string | null;
  ac_advertiser: string | null;
  ac_system: string | null;
  ac_username: string | null;
}

interface ACCheckSummary {
  ac_accounts_total: number;
  active_raketech_total: number;
  active_raketech_in_ac: number;
  active_raketech_not_in_ac: number;
  missing_sheet_total: number;
  missing_sheet_in_ac: number;
  missing_sheet_not_in_ac: number;
}

interface ACCheckData {
  active_raketech: ACCheckRow[];
  missing_sheet: ACCheckRow[];
  summary: ACCheckSummary;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
type Tab = 'raketech' | 'ac' | 'unknown' | 'invoice' | 'accheck';

const RaketechMissingAccounts: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('raketech');
  const [searchTerm, setSearchTerm] = useState('');

  const [acToken, setAcToken] = useState<string>(() => getStoredACToken() || '');
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenInputValue, setTokenInputValue] = useState('');
  const [showTokenValue, setShowTokenValue] = useState(false);

  // AC live check state (on-demand, separate from the main report)
  const [acCheckData, setAcCheckData] = useState<ACCheckData | null>(null);
  const [acCheckLoading, setAcCheckLoading] = useState(false);
  const [acCheckError, setAcCheckError] = useState<string | null>(null);
  const [acCheckSearchTerm, setAcCheckSearchTerm] = useState('');
  const [acCheckSourceFilter, setAcCheckSourceFilter] = useState<'all' | 'raketech_active' | 'missing_sheet'>('all');
  const [acCheckStatusFilter, setAcCheckStatusFilter] = useState<'all' | 'in_ac' | 'not_in_ac'>('all');

  const hasACToken = acToken.length > 0;

  const fetchData = useCallback(async () => {
    const token = getAdwstatsToken();
    if (!token) { toast.error('Not authenticated'); return; }
    setIsLoading(true);
    setError(null);
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (acToken) headers['X-Affiliation-Cloud-Token'] = acToken;
    try {
      const resp = await fetch('/api/raketech/missing-accounts', { headers });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || json.message || 'Request failed');
      setData(json.data as ReportData);
      if (json.data.ac_only?.length > 0 && json.data.raketech_unmatched?.length === 0) {
        setActiveTab('ac');
      }
    } catch (err: any) {
      setError(err.message);
      toast.error(`Failed to load: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [acToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyToken = () => {
    const t = tokenInputValue.trim();
    if (!t) return;
    setAcToken(t);
    storeACToken(t);
    setTokenInputValue('');
    setShowTokenInput(false);
    toast.success('AC token saved — reloading data...');
  };

  const clearToken = () => {
    setAcToken('');
    localStorage.removeItem(AC_TOKEN_KEY);
    toast.info('AC token cleared');
  };

  const filteredRaketech = useMemo(() => {
    const list = data?.raketech_unmatched || [];
    if (!searchTerm) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(a =>
      a.account_id?.toLowerCase().includes(q) ||
      a.offer?.toLowerCase().includes(q) ||
      a.username?.toLowerCase().includes(q) ||
      a.partner?.toLowerCase().includes(q) ||
      a.geo?.toLowerCase().includes(q) ||
      a.brand?.toLowerCase().includes(q) ||
      a.adwin_comment?.toLowerCase().includes(q)
    );
  }, [data, searchTerm]);

  const filteredAC = useMemo(() => {
    const list = data?.ac_only || [];
    if (!searchTerm) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(a =>
      a.program_name?.toLowerCase().includes(q) ||
      String(a.ac_id || '').includes(q)
    );
  }, [data, searchTerm]);

  const summary = data?.summary;

  // Count how many unmatched rows have verified_accounts data
  const verifiedCount = useMemo(() =>
    (data?.raketech_unmatched || []).filter(a => a.created_date || a.account_status).length,
  [data]);

  // Build a set of AC affiliate IDs already present in Raketech data (via rec_report_id = ac_id).
  // Any AC-only account whose affiliate_account_ids overlaps this set is already "known" from
  // the Raketech sheet — so we exclude it from the "Not in Raketech" tab.
  const raketechKnownAcIds = useMemo(() => {
    const ids = new Set<number>();
    for (const acc of data?.raketech_unmatched || []) {
      if (acc.ac_id) ids.add(Number(acc.ac_id));
    }
    return ids;
  }, [data]);

  // AC-only accounts that have NO overlap with any Raketech data — truly unknown.
  const unknownAC = useMemo(() =>
    (data?.ac_only || []).filter(acc =>
      acc.affiliate_account_ids.length === 0 ||
      !acc.affiliate_account_ids.some(id => raketechKnownAcIds.has(id))
    ),
  [data, raketechKnownAcIds]);

  const filteredUnknown = useMemo(() => {
    if (!searchTerm) return unknownAC;
    const q = searchTerm.toLowerCase();
    return unknownAC.filter(a =>
      a.program_name?.toLowerCase().includes(q) ||
      String(a.ac_id || '').includes(q)
    );
  }, [unknownAC, searchTerm]);

  const filteredInvoice = useMemo(() => {
    const list = data?.invoice_only || [];
    if (!searchTerm) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(a =>
      a.offer?.toLowerCase().includes(q) ||
      a.partner?.toLowerCase().includes(q) ||
      a.brand_name?.toLowerCase().includes(q) ||
      a.geo?.toLowerCase().includes(q)
    );
  }, [data, searchTerm]);

  const fetchACCheck = useCallback(async () => {
    const token = getAdwstatsToken();
    if (!token) { toast.error('Not authenticated'); return; }
    if (!acToken) { toast.error('AC token required — set it above first'); return; }
    setAcCheckLoading(true);
    setAcCheckError(null);
    try {
      const resp = await fetch('/api/raketech/ac-live-check', {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Affiliation-Cloud-Token': acToken,
        },
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || json.message || 'Request failed');
      setAcCheckData(json.data as ACCheckData);
    } catch (err: any) {
      setAcCheckError(err.message);
      toast.error(`AC check failed: ${err.message}`);
    } finally {
      setAcCheckLoading(false);
    }
  }, [acToken]);

  const allACCheckRows = useMemo(() => {
    if (!acCheckData) return [];
    const rows: ACCheckRow[] = [
      ...acCheckData.active_raketech,
      ...acCheckData.missing_sheet,
    ];
    return rows;
  }, [acCheckData]);

  const filteredACCheck = useMemo(() => {
    let rows = allACCheckRows;
    if (acCheckSourceFilter !== 'all') rows = rows.filter(r => r.source === acCheckSourceFilter);
    if (acCheckStatusFilter === 'in_ac') rows = rows.filter(r => r.in_ac);
    if (acCheckStatusFilter === 'not_in_ac') rows = rows.filter(r => !r.in_ac);
    if (acCheckSearchTerm) {
      const q = acCheckSearchTerm.toLowerCase();
      rows = rows.filter(r =>
        r.offer?.toLowerCase().includes(q) ||
        r.partner?.toLowerCase().includes(q) ||
        r.username?.toLowerCase().includes(q) ||
        r.geo?.toLowerCase().includes(q) ||
        r.ac_name?.toLowerCase().includes(q) ||
        r.rec_report_id?.includes(q)
      );
    }
    return rows;
  }, [allACCheckRows, acCheckSourceFilter, acCheckStatusFilter, acCheckSearchTerm]);

  return (
    <>
    <style>{`
      /* Light-mode tables for easy copy-paste into Google Sheets */
      .lrt { background: white !important; border-color: #e5e7eb !important; }
      .lrt table thead tr th,
      .lrt table tfoot tr td { background: #f9fafb !important; }
      .lrt table tbody tr { background: white !important; }
      .lrt table tbody tr:hover td { background: #eff6ff !important; }
      .lrt table thead tr th { border-bottom: 1px solid #e5e7eb; }
      .lrt table tbody tr td { border-bottom: 1px solid #f3f4f6; }
      .lrt table tfoot tr td { border-top: 2px solid #e5e7eb; border-bottom: none; }
      .lrt-hdr { background: white !important; border-bottom-color: #e5e7eb !important; }
    `}</style>
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <UserX className="h-7 w-7 text-rose-400" />
              Missing Accounts Report
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Accounts in Raketech or AC reporting that have no matching local record
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* AC Token Section */}
      <div className="mb-6 rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Key className={`h-4 w-4 ${hasACToken ? 'text-emerald-400' : 'text-zinc-500'}`} />
            <div>
              <p className="text-xs font-medium text-white">
                Affiliation Cloud Token
                {hasACToken && (
                  <span className="ml-2 text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded">
                    Active
                  </span>
                )}
              </p>
              <p className="text-[10px] text-zinc-500 mt-0.5">
                {hasACToken
                  ? 'AC reporting data will be included in the analysis'
                  : 'Provide an AC token to also show accounts missing from AC reporting'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasACToken && (
              <button onClick={clearToken} className="text-[11px] text-zinc-500 hover:text-red-400 transition-colors">
                Clear
              </button>
            )}
            <button
              onClick={() => setShowTokenInput(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,14%)] hover:bg-[hsl(225,15%,18%)] border border-[hsl(225,15%,22%)] rounded-lg transition-colors"
            >
              <Key className="h-3.5 w-3.5" />
              {hasACToken ? 'Update Token' : 'Set Token'}
            </button>
          </div>
        </div>

        {showTokenInput && (
          <div className="mt-3 pt-3 border-t border-[hsl(225,15%,16%)]">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showTokenValue ? 'text' : 'password'}
                  value={tokenInputValue}
                  onChange={e => setTokenInputValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyToken()}
                  placeholder="Paste AC bearer token here..."
                  className="w-full pl-3 pr-8 py-2 text-xs bg-[hsl(225,15%,8%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                />
                <button onClick={() => setShowTokenValue(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                  {showTokenValue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <button onClick={applyToken} disabled={!tokenInputValue.trim()} className="px-3 py-2 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg transition-colors">
                Apply
              </button>
              <button onClick={() => { setShowTokenInput(false); setTokenInputValue(''); }} className="px-3 py-2 text-xs text-zinc-400 hover:text-white bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading */}
      {isLoading && !data && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">Loading missing accounts data...</p>
            {hasACToken && (
              <p className="text-xs text-zinc-600 mt-1">Fetching monthly AC breakdown (recon + post-recon) — this may take 15–30s</p>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && !isLoading && !data && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-base font-semibold text-white mb-2">Failed to load report</h2>
          <p className="text-sm text-zinc-400">{error}</p>
        </div>
      )}

      {data && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-amber-400" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Raketech Unmatched</span>
              </div>
              <p className="text-2xl font-bold text-amber-400 tabular-nums">{summary!.raketech_unmatched_count}</p>
              <p className="text-[11px] text-amber-400/70 mt-1 tabular-nums">
                {fmtEUR(summary!.raketech_unmatched_earnings_eur)} untracked
              </p>
              {verifiedCount > 0 && (
                <p className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  {verifiedCount} in "Not Verified" sheet
                </p>
              )}
            </div>

            <div className={`rounded-xl border p-4 ${hasACToken ? 'border-rose-500/20 bg-rose-500/5' : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Cloud className={`h-4 w-4 ${hasACToken ? 'text-rose-400' : 'text-zinc-500'}`} />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">AC Only</span>
              </div>
              {hasACToken ? (
                <>
                  <p className="text-2xl font-bold text-rose-400 tabular-nums">{summary!.ac_only_count}</p>
                  <p className="text-[11px] text-rose-400/70 mt-1 tabular-nums">{fmtEUR(summary!.ac_only_commission_eur)} missing</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-zinc-600 tabular-nums">—</p>
                  <p className="text-[11px] text-zinc-600 mt-1">Set AC token to see</p>
                </>
              )}
            </div>

            <div className={`rounded-xl border p-4 ${hasACToken && unknownAC.length > 0 ? 'border-orange-500/20 bg-orange-500/5' : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className={`h-4 w-4 ${hasACToken && unknownAC.length > 0 ? 'text-orange-400' : 'text-zinc-500'}`} />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Not in Raketech</span>
              </div>
              {hasACToken ? (
                <>
                  <p className={`text-2xl font-bold tabular-nums ${unknownAC.length > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                    {unknownAC.length}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-1">
                    {unknownAC.length === 0 ? 'All AC accounts known' : 'AC accounts unknown to Raketech'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-zinc-600 tabular-nums">—</p>
                  <p className="text-[11px] text-zinc-600 mt-1">Set AC token to see</p>
                </>
              )}
            </div>

            <div className={`rounded-xl border p-4 ${(summary!.invoice_only_count ?? 0) > 0 ? 'border-blue-500/20 bg-blue-500/5' : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)]'}`}>
              <div className="flex items-center gap-2 mb-2">
                <FileText className={`h-4 w-4 ${(summary!.invoice_only_count ?? 0) > 0 ? 'text-blue-400' : 'text-zinc-500'}`} />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Invoice Only</span>
              </div>
              <p className={`text-2xl font-bold tabular-nums ${(summary!.invoice_only_count ?? 0) > 0 ? 'text-blue-400' : 'text-zinc-400'}`}>
                {summary!.invoice_only_count ?? 0}
              </p>
              <p className="text-[11px] text-zinc-500 mt-1 tabular-nums">
                {fmtEUR(summary!.invoice_only_revenue_eur ?? 0)} in sheets
              </p>
            </div>

            <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="h-4 w-4 text-rose-400" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Total Gap</span>
              </div>
              <p className="text-2xl font-bold text-rose-400 tabular-nums">{fmtEUR(summary!.total_missing_commission_eur)}</p>
              <p className="text-[11px] text-rose-400/70 mt-1">estimated untracked (EUR)</p>
            </div>

            <div className="rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-zinc-500" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Period</span>
              </div>
              <p className="text-xs text-zinc-300 font-medium">{summary!.date_range.from.slice(0, 7)} →</p>
              <p className="text-xs text-zinc-300 font-medium">{summary!.date_range.to.slice(0, 7)}</p>
              {summary!.ac_token_provided && (
                <p className="text-[10px] text-emerald-400/70 mt-1">{summary!.ac_total_accounts} AC accounts fetched</p>
              )}
            </div>
          </div>

          {/* AC fetch error */}
          {data.ac_fetch_error && (
            <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-400">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>AC data fetch failed: {data.ac_fetch_error}. Raketech data is still shown below.</span>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-[hsl(225,15%,18%)]">
            <button
              onClick={() => { setActiveTab('raketech'); setSearchTerm(''); }}
              className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${activeTab === 'raketech' ? 'border-amber-400 text-amber-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            >
              Raketech Unmatched
              <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'raketech' ? 'bg-amber-400/10 text-amber-400' : 'bg-zinc-700 text-zinc-400'}`}>
                {summary!.raketech_unmatched_count}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('ac'); setSearchTerm(''); }}
              className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${activeTab === 'ac' ? 'border-rose-400 text-rose-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            >
              AC Only
              {hasACToken ? (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'ac' ? 'bg-rose-400/10 text-rose-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  {summary!.ac_only_count}
                </span>
              ) : (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-zinc-700 text-zinc-600">token required</span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('unknown'); setSearchTerm(''); }}
              className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${activeTab === 'unknown' ? 'border-orange-400 text-orange-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            >
              Not in Raketech
              {hasACToken ? (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'unknown' ? 'bg-orange-400/10 text-orange-400' : unknownAC.length > 0 ? 'bg-orange-500/10 text-orange-400' : 'bg-zinc-700 text-zinc-400'}`}>
                  {unknownAC.length}
                </span>
              ) : (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-zinc-700 text-zinc-600">token required</span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('invoice'); setSearchTerm(''); }}
              className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${activeTab === 'invoice' ? 'border-blue-400 text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            >
              Invoice Only
              <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'invoice' ? 'bg-blue-400/10 text-blue-400' : (summary!.invoice_only_count ?? 0) > 0 ? 'bg-blue-500/10 text-blue-400' : 'bg-zinc-700 text-zinc-400'}`}>
                {summary!.invoice_only_count ?? 0}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('accheck'); setSearchTerm(''); }}
              className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${activeTab === 'accheck' ? 'border-emerald-400 text-emerald-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            >
              AC Status
              {acCheckData ? (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'accheck' ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  {acCheckData.summary.active_raketech_in_ac + acCheckData.summary.missing_sheet_in_ac}/{acCheckData.summary.active_raketech_total + acCheckData.summary.missing_sheet_total}
                </span>
              ) : (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-zinc-700 text-zinc-500">live</span>
              )}
            </button>
          </div>

          {/* Search */}
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={activeTab === 'raketech' ? 'Search accounts / offers...' : 'Search programs...'}
              className="pl-8 pr-3 py-1.5 w-full text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-rose-500/30"
            />
          </div>

          {/* ----------------------------------------------------------------
              Raketech Unmatched Table
          ---------------------------------------------------------------- */}
          {activeTab === 'raketech' && (
            <>
              {filteredRaketech.length === 0 ? (
                <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-8 text-center">
                  <Database className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-white mb-2">
                    {searchTerm ? 'No matching accounts' : 'All Raketech Accounts Matched'}
                  </h2>
                  <p className="text-sm text-zinc-400">
                    {searchTerm ? 'Try a different search term.' : 'Every Raketech account has a corresponding local record.'}
                  </p>
                </div>
              ) : (
                <div className="lrt bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
                  <div className="lrt-hdr px-4 py-3 border-b border-[hsl(225,15%,14%)] flex items-start justify-between gap-4">
                    <p className="text-xs text-gray-500">
                      One row per (account, offer). Each row is a distinct Raketech program with no local mapping.
                      Created Date and comments come from the <span className="text-gray-600">"Recon Accounts Not Verified"</span> sheet.
                      First/Last FTD and Commission months are derived from the monthly reconciliation data.
                    </p>
                    <div className="flex items-center gap-3 shrink-0 text-[10px] text-zinc-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />≤ 3 mo</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />≤ 12 mo</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-500 inline-block" />older</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(225,15%,8%)]">
                        <tr>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Partner</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Offer</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Username</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">GEO</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC ID</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Status</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Created</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Recon (EUR)</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First Comm.</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last Comm.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                        {filteredRaketech.map((acc, idx) => (
                          <tr key={`${acc.account_id}-${acc.offer}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                            <td className="px-3 py-2.5 text-gray-900 font-semibold whitespace-nowrap">{acc.partner || '—'}</td>
                            <td className="px-3 py-2.5 font-mono text-[10px] text-gray-600 max-w-[180px] truncate" title={acc.offer}>
                              {acc.offer || acc.account_id}
                            </td>
                            <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{acc.username || '—'}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              {acc.geo ? <span className="text-purple-700 font-medium">{acc.geo}</span> : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 font-mono tabular-nums whitespace-nowrap">
                              {acc.ac_id || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              {acc.account_status ? (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                  acc.account_status.toLowerCase() === 'active'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-300'
                                }`}>
                                  {acc.account_status}
                                </span>
                              ) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-center text-gray-600 whitespace-nowrap tabular-nums">
                              {fmtDate(acc.created_date)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-amber-700 tabular-nums whitespace-nowrap font-medium">
                              {acc.total_recon_earnings_eur > 0 ? fmtEUR(acc.total_recon_earnings_eur) : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className="text-gray-500">{fmtMonth(acc.first_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={recencyClass(acc.last_ftd_month)}>{fmtMonth(acc.last_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className="text-gray-500">{fmtMonth(acc.first_commission_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={recencyClass(acc.last_commission_month)}>{fmtMonth(acc.last_commission_month)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[hsl(225,15%,8%)] border-t border-[hsl(225,15%,18%)]">
                        <tr>
                          <td colSpan={7} className="px-3 py-2.5 text-gray-500 text-[10px] font-semibold uppercase tracking-wider">
                              Total ({filteredRaketech.length} offer rows)
                            </td>
                            <td className="px-3 py-2.5 text-right text-amber-700 font-semibold tabular-nums">
                              {fmtEUR(filteredRaketech.reduce((s, a) => s + a.total_recon_earnings_eur, 0))}
                            </td>
                            <td colSpan={4} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ----------------------------------------------------------------
              AC Only Table
          ---------------------------------------------------------------- */}
          {activeTab === 'ac' && (
            <>
              {!hasACToken ? (
                <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
                  <Key className="h-10 w-10 text-zinc-500 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-white mb-2">AC Token Required</h2>
                  <p className="text-sm text-zinc-400 mb-4">
                    Provide an Affiliation Cloud bearer token above to compare against AC reporting.
                  </p>
                  <button
                    onClick={() => setShowTokenInput(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                  >
                    <Key className="h-4 w-4" />
                    Set AC Token
                  </button>
                </div>
              ) : filteredAC.length === 0 ? (
                <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-8 text-center">
                  <Cloud className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-white mb-2">
                    {searchTerm ? 'No matching programs' : 'No AC-Only Accounts'}
                  </h2>
                  <p className="text-sm text-zinc-400">
                    {searchTerm ? 'Try a different search term.' : 'All AC reporting accounts have a corresponding local record.'}
                  </p>
                </div>
              ) : (
                <div className="lrt bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
                  <div className="lrt-hdr px-4 py-3 border-b border-[hsl(225,15%,14%)] flex items-start justify-between gap-4">
                    <p className="text-xs text-gray-500">
                      Accounts that appear in AC reporting but have no corresponding local adwstats account.
                      First/Last FTD and Payment months derived from monthly AC report data (Jan 2023 → now).
                    </p>
                    <div className="flex items-center gap-3 shrink-0 text-[10px] text-zinc-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />≤ 3 mo</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />≤ 12 mo</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-500 inline-block" />older</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(225,15%,8%)]">
                        <tr>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Program Name</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC ID</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Commission (EUR)</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">FTDs</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Signups</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Clicks</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First Payment</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last Payment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                        {filteredAC.map((acc, idx) => (
                          <tr key={`${acc.ac_id}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                            <td className="px-3 py-2.5 text-gray-900 font-semibold">{acc.program_name}</td>
                            <td className="px-3 py-2.5 text-gray-500 font-mono tabular-nums">{acc.ac_id ?? '—'}</td>
                            <td className="px-3 py-2.5 text-right text-rose-700 tabular-nums font-medium whitespace-nowrap">
                              {fmtEUR(acc.total_commission)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-700 tabular-nums">
                              {acc.total_ftds > 0 ? acc.total_ftds.toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-600 tabular-nums">
                              {acc.total_signups > 0 ? acc.total_signups.toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-500 tabular-nums">
                              {acc.total_clicks > 0 ? acc.total_clicks.toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className="text-gray-500">{fmtMonth(acc.first_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={recencyClass(acc.last_ftd_month)}>{fmtMonth(acc.last_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className="text-gray-500">{fmtMonth(acc.first_commission_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={recencyClass(acc.last_commission_month)}>{fmtMonth(acc.last_commission_month)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[hsl(225,15%,8%)] border-t border-[hsl(225,15%,18%)]">
                        <tr>
                          <td colSpan={2} className="px-3 py-2.5 text-gray-500 text-[10px] font-semibold uppercase tracking-wider">
                            Total ({filteredAC.length} programs)
                          </td>
                          <td className="px-3 py-2.5 text-right text-rose-700 font-semibold tabular-nums whitespace-nowrap">
                            {fmtEUR(filteredAC.reduce((s, a) => s + a.total_commission, 0))}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-700 font-semibold tabular-nums">
                            {filteredAC.reduce((s, a) => s + a.total_ftds, 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-600 font-semibold tabular-nums">
                            {filteredAC.reduce((s, a) => s + a.total_signups, 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-500 font-semibold tabular-nums">
                            {filteredAC.reduce((s, a) => s + a.total_clicks, 0).toLocaleString()}
                          </td>
                          <td colSpan={4} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ----------------------------------------------------------------
              Not in Raketech Tab
              AC-only accounts whose affiliate IDs have no match in any
              Raketech data source (reconciliation / partner / verified sheet).
              These are the truly "unknown" accounts — AC knows about them
              but they never appeared in any Raketech Google Sheet import.
          ---------------------------------------------------------------- */}
          {activeTab === 'unknown' && (
            <>
              {!hasACToken ? (
                <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
                  <Key className="h-10 w-10 text-zinc-500 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-white mb-2">AC Token Required</h2>
                  <p className="text-sm text-zinc-400 mb-4">
                    Provide an Affiliation Cloud bearer token above to compare against AC reporting.
                  </p>
                  <button
                    onClick={() => setShowTokenInput(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                  >
                    <Key className="h-4 w-4" />
                    Set AC Token
                  </button>
                </div>
              ) : filteredUnknown.length === 0 ? (
                <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-8 text-center">
                  <ShieldAlert className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-white mb-2">
                    {searchTerm ? 'No matching programs' : 'No Completely Unknown Accounts'}
                  </h2>
                  <p className="text-sm text-zinc-400">
                    {searchTerm
                      ? 'Try a different search term.'
                      : 'Every AC-only account is already referenced in the Raketech sheet data.'}
                  </p>
                </div>
              ) : (
                <div className="lrt bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
                  <div className="lrt-hdr px-4 py-3 border-b border-[hsl(225,15%,14%)] flex items-start justify-between gap-4">
                    <p className="text-xs text-gray-500">
                      AC accounts with commission activity that are <span className="text-orange-600">not referenced anywhere</span> in
                      the Raketech Google Sheet imports (no reconciliation row, no partner account entry, no verified sheet entry).
                      These are the most unknown — Raketech has never mentioned them in any exported data.
                    </p>
                    <div className="flex items-center gap-3 shrink-0 text-[10px] text-zinc-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />≤ 3 mo</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />≤ 12 mo</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-500 inline-block" />older</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(225,15%,8%)]">
                        <tr>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Program Name</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC ID</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Commission (EUR)</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">FTDs</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Signups</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First Payment</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last Payment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                        {filteredUnknown.map((acc, idx) => (
                          <tr key={`unknown-${acc.ac_id}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                            <td className="px-3 py-2.5 text-gray-900 font-semibold">{acc.program_name}</td>
                            <td className="px-3 py-2.5 text-gray-500 font-mono tabular-nums">{acc.ac_id ?? '—'}</td>
                            <td className="px-3 py-2.5 text-right text-orange-700 tabular-nums font-medium whitespace-nowrap">
                              {fmtEUR(acc.total_commission)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-700 tabular-nums">
                              {acc.total_ftds > 0 ? acc.total_ftds.toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-600 tabular-nums">
                              {acc.total_signups > 0 ? acc.total_signups.toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className="text-gray-500">{fmtMonth(acc.first_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={recencyClass(acc.last_ftd_month)}>{fmtMonth(acc.last_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className="text-gray-500">{fmtMonth(acc.first_commission_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={recencyClass(acc.last_commission_month)}>{fmtMonth(acc.last_commission_month)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[hsl(225,15%,8%)] border-t border-[hsl(225,15%,18%)]">
                        <tr>
                          <td colSpan={2} className="px-3 py-2.5 text-gray-500 text-[10px] font-semibold uppercase tracking-wider">
                            Total ({filteredUnknown.length} programs)
                          </td>
                          <td className="px-3 py-2.5 text-right text-orange-700 font-semibold tabular-nums whitespace-nowrap">
                            {fmtEUR(filteredUnknown.reduce((s, a) => s + a.total_commission, 0))}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-700 font-semibold tabular-nums">
                            {filteredUnknown.reduce((s, a) => s + a.total_ftds, 0).toLocaleString()}
                          </td>
                          <td colSpan={5} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ----------------------------------------------------------------
              Invoice Only Tab
              Offers from monthly commission sheets (e.g. "October 2025 Commission")
              that have no matching offer in the reconciliation or partner accounts.
              These are typically post-recon accounts (Dec 2024+) generating revenue
              that's invoiced but not yet tracked in adwstats.
          ---------------------------------------------------------------- */}
          {activeTab === 'invoice' && (
            <>
              {filteredInvoice.length === 0 ? (
                <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-8 text-center">
                  <FileText className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-white mb-2">
                    {searchTerm ? 'No matching offers' : 'No Invoice-Only Offers'}
                  </h2>
                  <p className="text-sm text-zinc-400">
                    {searchTerm
                      ? 'Try a different search term.'
                      : 'All monthly commission sheet offers are already captured in reconciliation or partner account data.'}
                  </p>
                </div>
              ) : (
                <div className="lrt bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
                  <div className="lrt-hdr px-4 py-3 border-b border-[hsl(225,15%,14%)] flex items-start justify-between gap-4">
                    <p className="text-xs text-gray-500">
                      Offers appearing in the <span className="text-blue-600">monthly commission sheets</span> (e.g. "October 2025 Commission")
                      that have <span className="text-gray-700">no matching offer</span> in the reconciliation or partner accounts data.
                      These are typically post-recon new accounts generating invoiced revenue not yet tracked in adwstats.
                      Revenue is in EUR (as stated in the sheet).
                    </p>
                    <div className="flex items-center gap-3 shrink-0 text-[10px] text-gray-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />recent</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />older</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(225,15%,8%)]">
                        <tr>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Match</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Partner</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Offer</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Brand</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">GEO</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Username</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC ID</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Acct Status</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Created</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">FTDs</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">QFTDs</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">CPA Rev.</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">RS Rev.</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">RS %</th>
                          <th className="text-right px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Total (EUR)</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Months</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                        {filteredInvoice.map((row, idx) => (
                          <tr key={`inv-${row.offer}-${row.geo}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              {row.match_status === 'unmatched' ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                                  Unmatched
                                </span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 rounded border bg-gray-100 text-gray-500 border-gray-300">
                                  Unknown
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-gray-900 font-semibold whitespace-nowrap">{row.partner || '—'}</td>
                            <td className="px-3 py-2.5 font-mono text-[10px] text-gray-600 max-w-[160px] truncate" title={row.offer}>
                              {row.offer || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{row.brand_name || '—'}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              {row.geo ? <span className="text-purple-700 font-medium">{row.geo}</span> : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                              {row.username || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 font-mono tabular-nums whitespace-nowrap">
                              {row.ac_id || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              {row.account_status ? (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                  row.account_status.toLowerCase() === 'active'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-300'
                                }`}>
                                  {row.account_status}
                                </span>
                              ) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-center text-gray-600 whitespace-nowrap tabular-nums">
                              {fmtDate(row.created_date)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-700 tabular-nums">
                              {row.total_ftds > 0 ? row.total_ftds.toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-600 tabular-nums">
                              {row.total_qftds > 0 ? row.total_qftds.toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-600 tabular-nums whitespace-nowrap">
                              {row.total_cpa_revenue > 0 ? fmtEUR(row.total_cpa_revenue) : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-600 tabular-nums whitespace-nowrap">
                              {row.total_rs_revenue > 0 ? fmtEUR(row.total_rs_revenue) : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-500 tabular-nums">
                              {row.avg_rs_pct > 0 ? `${row.avg_rs_pct}%` : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-blue-700 tabular-nums font-medium whitespace-nowrap">
                              {fmtEUR(row.total_revenue_eur)}
                            </td>
                            <td className="px-3 py-2.5 text-center text-gray-500 tabular-nums">
                              {row.month_count}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className="text-gray-500">{fmtMonth(row.first_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={recencyClass(row.last_month)}>{fmtMonth(row.last_month)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[hsl(225,15%,8%)] border-t border-[hsl(225,15%,18%)]">
                        <tr>
                          <td colSpan={9} className="px-3 py-2.5 text-gray-500 text-[10px] font-semibold uppercase tracking-wider">
                            Total ({filteredInvoice.length} offers)
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-700 font-semibold tabular-nums">
                            {filteredInvoice.reduce((s, a) => s + a.total_ftds, 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-600 font-semibold tabular-nums">
                            {filteredInvoice.reduce((s, a) => s + a.total_qftds, 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-600 font-semibold tabular-nums whitespace-nowrap">
                            {fmtEUR(filteredInvoice.reduce((s, a) => s + a.total_cpa_revenue, 0))}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-600 font-semibold tabular-nums whitespace-nowrap">
                            {fmtEUR(filteredInvoice.reduce((s, a) => s + a.total_rs_revenue, 0))}
                          </td>
                          <td />
                          <td className="px-3 py-2.5 text-right text-blue-700 font-semibold tabular-nums whitespace-nowrap">
                            {fmtEUR(filteredInvoice.reduce((s, a) => s + a.total_revenue_eur, 0))}
                          </td>
                          <td colSpan={3} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ----------------------------------------------------------------
              AC Status Tab
              Live check: which active Raketech accounts + "Missing Accounts"
              sheet entries can be found in the AC admin account listing.
          ---------------------------------------------------------------- */}
          {activeTab === 'accheck' && (
            <div className="space-y-4">
              {/* Summary cards + run button */}
              <div className="flex items-start gap-4 flex-wrap">
                {acCheckData ? (
                  <>
                    <div className="flex gap-3 flex-wrap">
                      <div className="bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,18%)] rounded-xl px-4 py-3 min-w-[140px]">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Active Raketech</p>
                        <p className="text-lg font-bold text-white">{acCheckData.summary.active_raketech_total}</p>
                        <p className="text-[11px] mt-0.5">
                          <span className="text-emerald-400">{acCheckData.summary.active_raketech_in_ac} in AC</span>
                          <span className="text-zinc-500 mx-1">·</span>
                          <span className="text-rose-400">{acCheckData.summary.active_raketech_not_in_ac} missing</span>
                        </p>
                      </div>
                      <div className="bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,18%)] rounded-xl px-4 py-3 min-w-[140px]">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Missing Sheet</p>
                        <p className="text-lg font-bold text-white">{acCheckData.summary.missing_sheet_total}</p>
                        <p className="text-[11px] mt-0.5">
                          <span className="text-emerald-400">{acCheckData.summary.missing_sheet_in_ac} in AC</span>
                          <span className="text-zinc-500 mx-1">·</span>
                          <span className="text-rose-400">{acCheckData.summary.missing_sheet_not_in_ac} missing</span>
                        </p>
                      </div>
                      <div className="bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,18%)] rounded-xl px-4 py-3 min-w-[120px]">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">AC Accounts</p>
                        <p className="text-lg font-bold text-zinc-300">{acCheckData.summary.ac_accounts_total}</p>
                        <p className="text-[11px] text-zinc-500 mt-0.5">total in AC admin</p>
                      </div>
                    </div>
                    <button
                      onClick={fetchACCheck}
                      disabled={acCheckLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50 self-start mt-1"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${acCheckLoading ? 'animate-spin' : ''}`} />
                      Re-run
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col items-start gap-3">
                    <p className="text-xs text-zinc-400 max-w-md">
                      Fetches all AC admin accounts and checks which active Raketech unmatched
                      accounts (and "Missing Accounts" sheet entries) exist in the AC listing.
                      Requires an active AC token.
                    </p>
                    <button
                      onClick={fetchACCheck}
                      disabled={acCheckLoading || !hasACToken}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg transition-colors"
                    >
                      {acCheckLoading
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Play className="h-4 w-4" />
                      }
                      {acCheckLoading ? 'Running AC check...' : 'Run AC Check'}
                    </button>
                    {!hasACToken && (
                      <p className="text-[11px] text-amber-400">Set an AC token above to enable this check.</p>
                    )}
                  </div>
                )}
              </div>

              {acCheckError && (
                <div className="flex items-center gap-2 px-4 py-3 bg-rose-500/10 border border-rose-500/20 rounded-lg text-xs text-rose-400">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {acCheckError}
                </div>
              )}

              {acCheckData && (
                <div className="lrt bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
                  {/* Filters */}
                  <div className="lrt-hdr px-4 py-3 border-b border-[hsl(225,15%,14%)] flex items-center gap-3 flex-wrap">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={acCheckSearchTerm}
                        onChange={e => setAcCheckSearchTerm(e.target.value)}
                        placeholder="Search accounts..."
                        className="pl-8 pr-3 py-1.5 text-xs bg-gray-100 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 w-48"
                      />
                    </div>
                    <div className="flex gap-1">
                      {(['all', 'raketech_active', 'missing_sheet'] as const).map(f => (
                        <button
                          key={f}
                          onClick={() => setAcCheckSourceFilter(f)}
                          className={`px-2.5 py-1 text-[11px] rounded border transition-colors ${acCheckSourceFilter === f ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-300 hover:border-gray-400'}`}
                        >
                          {f === 'all' ? 'All sources' : f === 'raketech_active' ? 'Active Raketech' : 'Missing Sheet'}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-1">
                      {(['all', 'in_ac', 'not_in_ac'] as const).map(f => (
                        <button
                          key={f}
                          onClick={() => setAcCheckStatusFilter(f)}
                          className={`px-2.5 py-1 text-[11px] rounded border transition-colors ${acCheckStatusFilter === f ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-300 hover:border-gray-400'}`}
                        >
                          {f === 'all' ? 'All' : f === 'in_ac' ? '✓ In AC' : '✗ Not in AC'}
                        </button>
                      ))}
                    </div>
                    <span className="ml-auto text-[11px] text-gray-400">{filteredACCheck.length} rows</span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(225,15%,8%)]">
                        <tr>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">In AC</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Source</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Partner</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Offer</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Username</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">GEO</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC ID</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Acct Status</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Created</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last FTD</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">First Comm.</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Last Comm.</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC Name</th>
                          <th className="text-center px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC Status</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">Advertiser</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">System</th>
                          <th className="text-left px-3 py-2.5 text-gray-500 font-semibold whitespace-nowrap">AC Username</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                        {filteredACCheck.map((row, idx) => (
                          <tr key={`ac-check-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                            <td className="px-3 py-2.5 text-center">
                              {row.in_ac ? '✅' : '❌'}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                row.source === 'raketech_active'
                                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                                  : 'bg-blue-50 text-blue-700 border-blue-200'
                              }`}>
                                {row.source === 'raketech_active' ? 'Active' : 'Sheet'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-gray-900 font-semibold whitespace-nowrap">{row.partner || '—'}</td>
                            <td className="px-3 py-2.5 font-mono text-[10px] text-gray-600 max-w-[180px] truncate" title={row.offer}>
                              {row.offer || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{row.username || '—'}</td>
                            <td className="px-3 py-2.5 text-center">
                              {row.geo ? <span className="text-purple-700 font-medium">{row.geo}</span> : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 font-mono tabular-nums whitespace-nowrap">
                              {row.rec_report_id || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              {row.account_status ? (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                  row.account_status.toLowerCase() === 'active'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-300'
                                }`}>
                                  {row.account_status}
                                </span>
                              ) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-center text-gray-600 whitespace-nowrap tabular-nums">
                              {fmtDate(row.created_date)}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap tabular-nums">
                              <span className="text-gray-500">{fmtMonth(row.first_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap tabular-nums">
                              <span className={recencyClass(row.last_ftd_month)}>{fmtMonth(row.last_ftd_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap tabular-nums">
                              <span className="text-gray-500">{fmtMonth(row.first_commission_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap tabular-nums">
                              <span className={recencyClass(row.last_commission_month)}>{fmtMonth(row.last_commission_month)}</span>
                            </td>
                            <td className="px-3 py-2.5 text-gray-800 whitespace-nowrap">
                              {row.ac_name || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              {row.ac_status ? (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                  row.ac_status.toLowerCase() === 'active'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-300'
                                }`}>
                                  {row.ac_status}
                                </span>
                              ) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                              {row.ac_advertiser || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                              {row.ac_system || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                              {row.ac_username || <span className="text-gray-400">—</span>}
                            </td>
                          </tr>
                        ))}
                        {filteredACCheck.length === 0 && (
                          <tr>
                            <td colSpan={18} className="px-4 py-8 text-center text-gray-400">
                              No rows match the current filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-[hsl(225,15%,8%)] border-t border-[hsl(225,15%,18%)]">
                        <tr>
                          <td className="px-3 py-2.5 text-center">
                            <span className="text-emerald-600 font-semibold">{filteredACCheck.filter(r => r.in_ac).length}</span>
                            <span className="text-gray-400"> / </span>
                            <span className="text-rose-600 font-semibold">{filteredACCheck.filter(r => !r.in_ac).length}</span>
                          </td>
                          <td colSpan={13} className="px-3 py-2.5 text-gray-500 text-[10px] font-semibold uppercase tracking-wider">
                            {filteredACCheck.filter(r => r.in_ac).length} in AC · {filteredACCheck.filter(r => !r.in_ac).length} not in AC — {filteredACCheck.length} rows shown
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  </>
  );
};

export default RaketechMissingAccounts;
